import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import apiClient from "../helpers/apiClient";

const CategoriesContext = createContext();

export const useCategoriesContext = () => {
    const context = useContext(CategoriesContext);
    if (!context) {
        throw new Error("useCategoriesContext must be used within a CategoriesProvider");
    }
    return context;
};

export const CategoriesProvider = ({ children }) => {
    // Categories state
    const [categories, setCategories] = useState([]);
    const [totalCategories, setTotalCategories] = useState(0);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    // Locations state
    const [locations, setLocations] = useState([]);
    const [totalLocations, setTotalLocations] = useState(0);
    const [locationsLoading, setLocationsLoading] = useState(false);

    const categoriesInflightRef = useRef(new Map());
    const locationsInflightRef = useRef(new Map());

    // Categories functions
    const fetchCategories = useCallback(async (search = {}, pagination = {}) => {
        const { page, limit } = pagination;
        const queryParams = new URLSearchParams();

            if (typeof search === "string") {
                if (search.trim()) {
                    queryParams.append("searchTerm", search);
                }
            } else if (search && typeof search === "object") {
                Object.entries(search).forEach(([key, value]) => {
                    if (value != null && String(value).trim() !== "") {
                        queryParams.append(key, String(value));
                    }
                });
            }

            if (page != null) {
                queryParams.append("page", page.toString());
            }
            if (limit != null) {
                queryParams.append("limit", limit.toString());
            }

            const requestKey = queryParams.toString();
            if (categoriesInflightRef.current.has(requestKey)) {
                return categoriesInflightRef.current.get(requestKey);
            }

            const requestPromise = (async () => {
                try {
                    setCategoriesLoading(true);
                    const response = await apiClient.get(`/api/car-category-v2/list?${queryParams}`);
                    if (response?.data?.success) {
                        const categoryData = response?.data?.data || [];
                        const total = response?.data?.meta?.total ?? categoryData.length;
                        setCategories(categoryData);
                        setTotalCategories(total);
                        return { data: categoryData, total };
                    }
                    return { data: [], total: 0 };
                } catch (_error) {
                    setCategories([]);
                    setTotalCategories(0);
                    return { data: [], total: 0 };
                } finally {
                    setCategoriesLoading(false);
                    categoriesInflightRef.current.delete(requestKey);
                }
            })();

            categoriesInflightRef.current.set(requestKey, requestPromise);
            return requestPromise;
    }, []);

    const updateCategoryCount = useCallback((count) => {
        setTotalCategories(count);
    }, []);

    const addCategory = useCallback((newCategory) => {
        setCategories((prev) => [...prev, newCategory]);
        setTotalCategories((prev) => prev + 1);
    }, []);

    const updateCategory = useCallback((categoryId, updatedCategory) => {
        setCategories((prev) =>
            prev.map((category) =>
                category.categoryId === categoryId ? { ...category, ...updatedCategory } : category,
            ),
        );
    }, []);

    const removeCategory = useCallback((categoryId) => {
        setCategories((prev) => prev.filter((category) => category.categoryId !== categoryId));
        setTotalCategories((prev) => prev - 1);
    }, []);

    // Locations functions
    const fetchLocations = useCallback(async (searchTerm = "", limit = 100, page = 1) => {
        const queryParams = new URLSearchParams();
        if (searchTerm?.trim()) {
            queryParams.append("searchTerm", searchTerm);
        }
        queryParams.append("limit", limit.toString());
        queryParams.append("page", page.toString());

        const requestKey = queryParams.toString();
        if (locationsInflightRef.current.has(requestKey)) {
            return locationsInflightRef.current.get(requestKey);
        }

        const requestPromise = (async () => {
            try {
                setLocationsLoading(true);
                const response = await apiClient.get(`/api/location/list?${queryParams}`);
                if (response?.data?.success) {
                    const locationData = response?.data?.data || [];
                    setLocations(locationData);
                    setTotalLocations(response?.data?.meta?.total || locationData.length);
                    return locationData;
                }
                return [];
            } catch (_error) {
                setLocations([]);
                setTotalLocations(0);
                return [];
            } finally {
                setLocationsLoading(false);
                locationsInflightRef.current.delete(requestKey);
            }
        })();

        locationsInflightRef.current.set(requestKey, requestPromise);
        return requestPromise;
    }, []);

    const fetchAllLocations = useCallback(async () => {
        try {
            setLocationsLoading(true);
            const response = await apiClient.get("/api/location/list?limit=1000&page=1");
            if (response?.data?.success) {
                const locationData = response?.data?.data || [];
                setLocations(locationData);
                setTotalLocations(locationData.length);
                return locationData;
            }
            return [];
        } catch (_error) {
            setLocations([]);
            setTotalLocations(0);
            return [];
        } finally {
            setLocationsLoading(false);
        }
    }, []);

    const updateLocationCount = useCallback((count) => {
        setTotalLocations(count);
    }, []);

    const addLocation = useCallback((newLocation) => {
        setLocations((prev) => [...prev, newLocation]);
        setTotalLocations((prev) => prev + 1);
    }, []);

    const updateLocation = useCallback((locationId, updatedLocation) => {
        setLocations((prev) =>
            prev.map((location) =>
                location.locationId === locationId ? { ...location, ...updatedLocation } : location,
            ),
        );
    }, []);

    const removeLocation = useCallback((locationId) => {
        setLocations((prev) => prev.filter((location) => location.locationId !== locationId));
        setTotalLocations((prev) => prev - 1);
    }, []);

    const getLocationById = useCallback(
        (locationId) => locations.find((location) => location.locationId === locationId),
        [locations],
    );

    const getCategoryById = useCallback(
        (categoryId) => categories.find((category) => category.categoryId === categoryId),
        [categories],
    );

    /** Fresh API check for add-car gates; updates totals only (does not replace list caches). */
    const verifyCarCreationPrerequisites = useCallback(async () => {
        try {
            const [categoryResponse, locationResponse] = await Promise.all([
                apiClient.get("/api/car-category-v2/list?limit=1&page=1"),
                apiClient.get("/api/location/list?limit=1&page=1"),
            ]);

            let categoryTotal = 0;
            if (categoryResponse?.data?.success) {
                const categoryData = categoryResponse?.data?.data || [];
                categoryTotal = categoryResponse?.data?.meta?.total ?? categoryData.length;
                setTotalCategories(categoryTotal);
            }

            let locationTotal = 0;
            if (locationResponse?.data?.success) {
                const locationData = locationResponse?.data?.data || [];
                locationTotal = locationResponse?.data?.meta?.total ?? locationData.length;
                setTotalLocations(locationTotal);
            }

            return {
                hasCategories: categoryTotal > 0,
                hasLocations: locationTotal > 0,
            };
        } catch (_error) {
            return {
                hasCategories: false,
                hasLocations: false,
            };
        }
    }, []);

    // Combined loading state
    const categoryLoading = categoriesLoading || locationsLoading;

    const value = {
        // Categories
        categories,
        totalCategories,
        categoriesLoading,
        fetchCategories,
        updateCategoryCount,
        addCategory,
        updateCategory,
        removeCategory,
        getCategoryById,
        setCategories,
        setTotalCategories,

        // Locations
        locations,
        totalLocations,
        locationsLoading,
        fetchLocations,
        fetchAllLocations,
        updateLocationCount,
        addLocation,
        updateLocation,
        removeLocation,
        getLocationById,
        setLocations,
        setTotalLocations,

        // Combined
        categoryLoading,
        verifyCarCreationPrerequisites,
    };

    return <CategoriesContext.Provider value={value}>{children}</CategoriesContext.Provider>;
};

CategoriesProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
